import { Factory, ShoppingCart, Boxes, GitMerge, Cog, Package, ShoppingBag, Users2 } from "lucide-react";

const CLOSED_PO = new Set(["Completed", "Cancelled"]);
const CLOSED_SO = new Set(["Fulfilled", "Cancelled"]);
const isPast = (d, now) => d && new Date(d).getTime() < now;

// Aggregate live health numbers for the System Map from raw dashboard data.
export function systemHealth({ bomItems, salesOrders, orders, machines, poSummary }) {
  const now = Date.now();
  const componentIds = new Set(bomItems.flatMap((b) => (b.components || []).map((c) => c.item_id)));
  const active = bomItems.filter((b) => b.status !== "archived");
  const openSales = salesOrders.filter((s) => !CLOSED_SO.has(s.status));
  return {
    raw: active.filter((b) => b.item_type === "raw").length,
    recipes: active.filter((b) => b.item_type === "manufactured").length,
    fg: active.filter((b) => b.item_type === "manufactured" && !componentIds.has(b.id)).length,
    lowStock: active.filter((b) => (b.min_stock || 0) > 0 && (b.stock_quantity || 0) < b.min_stock).length,
    archived: bomItems.length - active.length,
    activeProd: orders.filter((o) => !CLOSED_PO.has(o.status)).length,
    openSales: openSales.length,
    overdue: openSales.filter((s) => isPast(s.required_delivery_date || s.expected_delivery, now)).length,
    pendingPO: poSummary.pending_approval_count || 0,
    lines: machines.length,
  };
}

const flag = (n, text) => (n ? `${n} ${text}` : null);
const tone = (n, bad = "red") => (n ? bad : "green");

export function systemNodes(h, { suppliers, customers, orders }) {
  return [
    { id: "suppliers", x: 24, y: 56, icon: Factory, accent: "slate", title: "Suppliers", kicker: "who you buy from", stat: `${suppliers.length} on file`, tab: "suppliers", health: suppliers.length ? "green" : "slate" },
    { id: "purchasing", x: 365, y: 56, icon: ShoppingCart, accent: "blue", title: "Purchase Orders", kicker: "replenish raw stock", stat: "reorder & receive", tab: "purchasing", alert: flag(h.pendingPO, "to approve"), alertTone: "amber", health: tone(h.pendingPO, "amber") },
    { id: "raw", x: 706, y: 56, icon: Boxes, accent: "amber", title: "Raw Inventory", kicker: "components & materials", stat: `${h.raw} raw items`, tab: "bom", alert: flag(h.lowStock, "low"), alertTone: "red", health: tone(h.lowStock) },
    { id: "bom", x: 365, y: 248, icon: GitMerge, accent: "violet", title: "BOM Recipes", kicker: "how products are built", stat: `${h.recipes} recipes`, tab: "bom", alert: flag(h.archived, "archived"), alertTone: "slate", health: "green" },
    { id: "production", x: 706, y: 248, icon: Cog, accent: "indigo", title: "Production", kicker: `on ${h.lines} lines`, stat: `${h.activeProd} active / ${orders.length}`, tab: "schedule", health: "green" },
    { id: "finished", x: 706, y: 440, icon: Package, accent: "emerald", title: "Finished Goods", kicker: "ready to sell", stat: `${h.fg} products`, tab: "bom", health: "green" },
    { id: "sales", x: 365, y: 440, icon: ShoppingBag, accent: "teal", title: "Sales Orders", kicker: "ship finished goods", stat: `${h.openSales} open`, tab: "sales", alert: flag(h.overdue, "overdue"), alertTone: "red", health: tone(h.overdue) },
    { id: "customers", x: 24, y: 440, icon: Users2, accent: "rose", title: "Customers", kicker: "who you sell to", stat: `${customers.length} on file`, tab: "customers", health: customers.length ? "green" : "slate" },
  ];
}

export const SYSTEM_EDGES = [
  { d: "M234,106 L359,106", c: "#3b82f6" }, { d: "M575,106 L700,106", c: "#3b82f6" },
  { d: "M811,156 L811,242", c: "#6366f1" }, { d: "M575,298 L700,298", c: "#6366f1" },
  { d: "M811,348 L811,434", c: "#10b981" }, { d: "M706,490 L581,490", c: "#10b981" },
  { d: "M365,490 L240,490", c: "#10b981" }, { d: "M811,56 C811,14 470,14 470,50", c: "#f59e0b" },
];

export const SYSTEM_EDGE_LABELS = [
  { x: 300, y: 106, text: "supplies" }, { x: 640, y: 106, text: "receive" }, { x: 811, y: 199, text: "consumes" },
  { x: 640, y: 298, text: "defines recipe" }, { x: 811, y: 391, text: "produces" }, { x: 643, y: 490, text: "ships" },
  { x: 302, y: 490, text: "sold to" }, { x: 640, y: 16, text: "reorder when stock is low", tone: "amber" },
];
